<template>
    <div class="growni-loader" aria-hidden="false">
        <!-- Instant feedback: the bar shows from the first millisecond of a navigation. -->
        <Transition name="growni-loader-bar">
            <div
                v-if="isLoading"
                class="growni-loader__bar"
                :style="{ width: `${progress}%`, backgroundColor: section.color }"
            />
        </Transition>

        <!-- The card is the reward for an actually slow navigation. It waits out
             SHOW_AFTER_MS so quick transitions never flash a spinner. -->
        <Transition name="growni-loader-card">
            <div v-if="showCard" class="growni-loader__card" role="status" aria-live="polite">
                <div class="growni-loader__sprout" :style="{ color: section.color }">
                    <span class="growni-loader__soil" />
                    <span class="growni-loader__stem" />
                    <span class="growni-loader__leaf growni-loader__leaf--left" />
                    <span class="growni-loader__leaf growni-loader__leaf--right" />
                </div>

                <div class="growni-loader__text">
                    <p class="growni-loader__section" :style="{ color: section.color }">{{ section.label }}</p>
                    <Transition name="growni-loader-fact" mode="out-in">
                        <p :key="fact" class="growni-loader__fact">{{ fact }}</p>
                    </Transition>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { useGrowniLoader } from '@/composables/useGrowniLoader'

// Below this, a navigation feels instant and any overlay reads as a flash.
const SHOW_AFTER_MS = 350
const FACT_ROTATE_MS = 2600

const { isLoading, progress } = useLoadingIndicator()
const { facts, sectionFor, warmUpStats } = useGrowniLoader()
const router = useRouter()

const targetPath = ref(router.currentRoute.value.path)
const showCard = ref(false)
const factIndex = ref(Math.floor(Math.random() * facts.value.length))

const section = computed(() => sectionFor(targetPath.value))
const fact = computed(() => facts.value[factIndex.value % facts.value.length])

let showTimer: ReturnType<typeof setTimeout> | undefined
let rotateTimer: ReturnType<typeof setInterval> | undefined

// Knowing the destination early is what lets the loader wear the right colour.
router.beforeEach((to) => {
    targetPath.value = to.path
})

function nextFact() {
    if (facts.value.length < 2) return
    let next = factIndex.value
    // Never show the same line twice in a row — that reads as a frozen screen.
    while (next === factIndex.value) {
        next = Math.floor(Math.random() * facts.value.length)
    }
    factIndex.value = next
}

function stopTimers() {
    if (showTimer) clearTimeout(showTimer)
    if (rotateTimer) clearInterval(rotateTimer)
    showTimer = undefined
    rotateTimer = undefined
}

watch(isLoading, (loading) => {
    stopTimers()

    if (!loading) {
        showCard.value = false
        return
    }

    showTimer = setTimeout(() => {
        nextFact()
        showCard.value = true
        rotateTimer = setInterval(nextFact, FACT_ROTATE_MS)
    }, SHOW_AFTER_MS)
})

onMounted(warmUpStats)
onBeforeUnmount(stopTimers)
</script>

<style scoped>
.growni-loader__bar {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 100;
    height: 3px;
    border-radius: 0 3px 3px 0;
    transition:
        width 0.15s ease,
        background-color 0.3s ease;
    pointer-events: none;
}

.growni-loader__card {
    position: fixed;
    bottom: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 99;
    display: flex;
    align-items: center;
    gap: 0.875rem;
    max-width: min(30rem, calc(100vw - 2rem));
    padding: 0.875rem 1.25rem;
    border-radius: 999px;
    background: rgb(255 255 255 / 96%);
    box-shadow: 0 8px 30px rgb(0 0 0 / 12%);
    backdrop-filter: blur(8px);
    /* Never intercept a click — the page underneath stays usable. */
    pointer-events: none;
}

.growni-loader__text {
    min-width: 0;
}

.growni-loader__section {
    margin: 0 0 0.125rem;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.growni-loader__fact {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.35;
    color: #3f3f46;
}

/* --- the sprout ------------------------------------------------------- */
.growni-loader__sprout {
    position: relative;
    flex: none;
    width: 26px;
    height: 26px;
}

.growni-loader__soil {
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 18px;
    height: 2px;
    border-radius: 2px;
    background: currentcolor;
    opacity: 0.25;
    transform: translateX(-50%);
}

.growni-loader__stem {
    position: absolute;
    bottom: 2px;
    left: 50%;
    width: 2px;
    height: 16px;
    border-radius: 2px;
    background: currentcolor;
    transform: translateX(-50%);
    transform-origin: bottom center;
    animation: growni-stem 2.4s ease-in-out infinite;
}

.growni-loader__leaf {
    position: absolute;
    width: 9px;
    height: 9px;
    background: currentcolor;
    transform-origin: bottom center;
}

.growni-loader__leaf--left {
    bottom: 12px;
    left: 2px;
    border-radius: 100% 0 100% 0;
    animation: growni-leaf-left 2.4s ease-in-out infinite;
}

.growni-loader__leaf--right {
    right: 2px;
    bottom: 15px;
    border-radius: 0 100% 0 100%;
    animation: growni-leaf-right 2.4s ease-in-out infinite;
}

@keyframes growni-stem {
    0%,
    100% {
        transform: translateX(-50%) scaleY(0.35);
    }
    55% {
        transform: translateX(-50%) scaleY(1);
    }
}

@keyframes growni-leaf-left {
    0%,
    22% {
        opacity: 0;
        transform: scale(0.2) rotate(-25deg);
    }
    55%,
    100% {
        opacity: 1;
        transform: scale(1) rotate(0deg);
    }
}

@keyframes growni-leaf-right {
    0%,
    38% {
        opacity: 0;
        transform: scale(0.2) rotate(25deg);
    }
    70%,
    100% {
        opacity: 1;
        transform: scale(1) rotate(0deg);
    }
}

/* --- transitions ------------------------------------------------------ */
.growni-loader-bar-enter-active,
.growni-loader-bar-leave-active {
    transition: opacity 0.3s ease;
}

.growni-loader-bar-enter-from,
.growni-loader-bar-leave-to {
    opacity: 0;
}

.growni-loader-card-enter-active {
    transition:
        opacity 0.25s ease,
        transform 0.25s ease;
}

.growni-loader-card-leave-active {
    transition:
        opacity 0.18s ease,
        transform 0.18s ease;
}

.growni-loader-card-enter-from,
.growni-loader-card-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(0.75rem);
}

.growni-loader-fact-enter-active,
.growni-loader-fact-leave-active {
    transition:
        opacity 0.2s ease,
        transform 0.2s ease;
}

.growni-loader-fact-enter-from {
    opacity: 0;
    transform: translateY(0.35rem);
}

.growni-loader-fact-leave-to {
    opacity: 0;
    transform: translateY(-0.35rem);
}

/* Motion is decoration here; the text carries the meaning. */
@media (prefers-reduced-motion: reduce) {
    .growni-loader__stem,
    .growni-loader__leaf--left,
    .growni-loader__leaf--right {
        animation: none;
    }

    .growni-loader__leaf--left,
    .growni-loader__leaf--right {
        opacity: 1;
        transform: none;
    }

    .growni-loader-card-enter-active,
    .growni-loader-card-leave-active,
    .growni-loader-fact-enter-active,
    .growni-loader-fact-leave-active {
        transition: opacity 0.15s ease;
    }
}

[data-theme="dark"] .growni-loader__card {
    background: var(--color-surface-card);
}

[data-theme="dark"] .growni-loader__fact {
    color: var(--color-txt-body);
}
</style>
